import React from "react";
import { WSFlexBox, WSText } from "@wingspanhq/fe-component-library";
import {
  ICollaboratorSchema,
  IMemberClientFormW9Info
} from "@wingspanhq/payments/dist/interfaces";
import { CompanyStructure } from "@wingspanhq/users";
import { ComponentProps } from "../../components/FormFieldList";

import { ListValueType } from "../../components/LabelValueList";
import { Tooltip } from "../../components/Tooltip";
import {
  getCollaboratorEmail,
  getCollaboratorPhone
} from "../../query/payments/selectors";
import { TaxID } from "../components/TaxID";
import getTin from "./getTin";

const TinLabel = () => (
  <WSFlexBox alignItems="center">
    <WSText mr="XS" color="grey600">
      TIN
    </WSText>
    <Tooltip>Taxpayer Identification Number (EIN or SSN)</Tooltip>
  </WSFlexBox>
);

const getTinDisplayItem = (collaborator: ICollaboratorSchema) => {
  const tin = getTin(collaborator);

  return {
    id: "tin",
    name: "tin",
    label: <TinLabel />,
    value: tin && <TaxID taxId={tin} taxStatus={collaborator.taxStatus} />,
    type: ListValueType.TIN,
    componentProps: { formatOptions: { numericOnly: true } }
  };
};

export type PayeeInfoW9Item = {
  id: string;
  label: string;
  type: ListValueType;
  componentProps?: ComponentProps;
} & (
  | {
      name:
        | "ssn"
        | "ein"
        | "legalBusinessName"
        | "addressLine1"
        | "addressLine2"
        | "city"
        | "state";
      value: string | undefined;
    }
  | {
      name: "companyStructure";
      value: CompanyStructure | undefined;
    }
);

const commonTinFormatOptions = {
  numericOnly: true,
  delimiter: "-"
};

const getTinEditableItems = (
  formW9Data?: IMemberClientFormW9Info
): PayeeInfoW9Item[] => [
  {
    id: "ssn",
    name: "ssn" as const,
    label: "Social Security Number (SSN)",
    value: formW9Data?.ssn || "",
    type: ListValueType.Text,
    componentProps: {
      formatOptions: {
        ...commonTinFormatOptions,
        blocks: [3, 2, 4]
      }
    }
  },
  {
    id: "ein",
    name: "ein" as const,
    label: "Employer Identification Number (EIN)",
    value: formW9Data?.ein || "",
    type: ListValueType.Text,
    componentProps: {
      formatOptions: {
        ...commonTinFormatOptions,
        blocks: [2, 7],
        delimiter: "-"
      }
    }
  }
];

const getPayeeInfoBaseW9Items = (
  collaborator: ICollaboratorSchema
): PayeeInfoW9Item[] => {
  const { formW9Data, member } = collaborator;

  const legalBusinessName =
    formW9Data?.legalBusinessName ||
    member?.profile?.company?.legalBusinessName;
  const addressLine1 =
    formW9Data?.addressLine1 || member?.profile?.address?.addressLine1;
  const addressLine2 =
    formW9Data?.addressLine2 || member?.profile?.address?.addressLine2;
  const city = formW9Data?.city || member?.profile?.address?.city;
  const rawState = formW9Data?.state || member?.profile?.address?.state;

  return [
    {
      name: "legalBusinessName" as const,
      label: "Legal Business Name",
      type: ListValueType.Select,
      value: legalBusinessName
    },
    {
      name: "addressLine1" as const,
      label: "Address line 1",
      value: addressLine1
    },
    {
      name: "addressLine2" as const,
      label: "Address line 2",
      value: addressLine2
    },
    {
      name: "city" as const,
      label: "City",
      value: city
    }
  ].map(item => ({ ...item, id: item.name, type: ListValueType.Text }));
};

export const getPayeeInfoOtherItems = (collaborator: ICollaboratorSchema) =>
  [
    {
      name: "emailAddress",
      label: "Email address",
      value: getCollaboratorEmail(collaborator),
      componentProps: { type: "email" }
    },
    {
      name: "phone",
      label: "Phone",
      value:
        getCollaboratorPhone(collaborator) ||
        collaborator.labels?.phoneNumber ||
        "-"
    }
  ].map(item => ({ ...item, id: item.name, type: ListValueType.Text }));

export const getPayeeInfoDisplayItems = (collaborator: ICollaboratorSchema) => [
  getTinDisplayItem(collaborator),
  ...getPayeeInfoBaseW9Items(collaborator),
  ...getPayeeInfoOtherItems(collaborator)
];

export const getPayeeInfoW9EditItems = (
  collaborator: ICollaboratorSchema
): PayeeInfoW9Item[] => [
  ...getTinEditableItems(collaborator.formW9Data),
  ...getPayeeInfoBaseW9Items(collaborator)
];
